import React, { useMemo } from "react";
import { compose, withProps } from "recompose";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
  UncontrolledButtonDropdown,
} from "reactstrap";
import { withDocumentRenderer } from "components";
import { RemittanceDocument, ReceiptDocument } from "components/documents";
import { Page, FormGroup } from "components/common";
import { formatDate, formatCurrency } from "services/util";
import { Trans, useTranslation } from "react-i18next";
import { Link, useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { useXeroClient } from "../../../hooks";
import { NotificationManager } from "react-notifications";
import { PendingButton } from "../../../components/common";
import { formatAccountCurrency } from "../../../services/util";

const TH = ({ children, ...props }) => (
  <th {...props}>
    <Trans>{children}</Trans>
  </th>
);

export const Payment = ({
  state,
  DownloadDocument,
  DocumentRenderer,
  EmailDocument,
  documentType,
  document,
}) => {
  const { t } = useTranslation();
  const client = useXeroClient();
  const history = useHistory();
  const { mutate: onRemoveAndRedo, isLoading } = useMutation(
    () => {
      return client.put(`payments/${state.PaymentID}`, {
        PaymentID: state.PaymentID,
        Status: "DELETED",
      });
    },
    {
      useErrorBoundary: false,
      onSuccess: () => {
        NotificationManager.success(t("The account transaction has been removed"));
        if (state.Invoice) {
          if (state.Invoice.Type === "ACCREC") {
            history.push(`/accounts_receivable/invoices/edit/${state.Invoice.InvoiceID}`);
          } else if (state.Invoice.Type === "ACCPAY") {
            history.push(`/purchases/bills/edit/${state.Invoice.InvoiceID}`);
          }
        }
      },
    }
  );

  const { Invoice, Status, Date, Reference, Amount } = state;
  const { Type, InvoiceNumber, InvoiceID, Contact, DueDate, Total } = Invoice;

  const isInvoice = Type === "ACCPAY" || Type === "ACCREC";
  const isCreditNote = Type === "ACCRECCREDIT" || Type === "ACCPAYCREDIT";

  return (
    <Page title="Transaction: Payment">
      {Status === "AUTHORISED" ? (
        <div className="d-flex flex-direction-row float-right">
          {document ? (
            <>
              <DownloadDocument className="mr-2" />
              <EmailDocument item={state} type={documentType} />
              <DocumentRenderer component={document} />
            </>
          ) : null}
          <UncontrolledButtonDropdown className="ml-2">
            <DropdownToggle caret color="primary" className="btn-sm">
              <Trans>Options</Trans>
            </DropdownToggle>
            <DropdownMenu className="p-0">
              <DropdownItem
                tag={PendingButton}
                pending={isLoading}
                onClick={onRemoveAndRedo}
              >
                <Trans>Remove & Redo</Trans>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </div>
      ) : (
        <p className="text-danger">
          <Trans>Deleted</Trans>
        </p>
      )}
      <div className="d-flex float-left">
        <FormGroup
          label="Payment Date"
          className="mb-0"
          labelProps={{ className: "font-weight-bold" }}
        >
          <div className="form-control-plaintext">{formatDate(Date)}</div>
        </FormGroup>
        <FormGroup
          label="Reference"
          className="ml-3 mb-0"
          labelProps={{ className: "font-weight-bold" }}
        >
          <div className="form-control-plaintext">{Reference}</div>
        </FormGroup>
      </div>
      <hr />
      <Table size="sm" responsive>
        <thead>
          <tr>
            <TH>Contact</TH>
            {isInvoice && <TH>Inv #</TH>}
            {isCreditNote && (
              <th>
                <Trans>Credit Note #</Trans>
              </th>
            )}
            <TH>Date</TH>
            {isInvoice && <TH>Due Date</TH>}
            {isInvoice && <TH className="text-right">Total</TH>}
            {isCreditNote && (
              <th className="text-right">
                <Trans>Credit Note Total</Trans>
              </th>
            )}
            <TH className="text-right">Payment Amount</TH>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{Contact.Name}</td>
            {isInvoice && (
              <td>
                {Type === "ACCPAY" && (
                  <Link to={`/purchases/bills/edit/${InvoiceID}`}>
                    {Invoice.HesaabNumber || InvoiceNumber || <Trans>(No reference number)</Trans>}
                  </Link>
                )}
                {Type === "ACCREC" && (
                  <Link to={`/accounts_receivable/invoices/edit/${InvoiceID}`}>
                    {Invoice.HesaabNumber || InvoiceNumber || <Trans>(No reference number)</Trans>}
                  </Link>
                )}
              </td>
            )}
            {isCreditNote && (
              <td>
                {Type === "ACCPAYCREDIT" && (
                  <Link to={`/accounts_payable/credit_notes/edit/${InvoiceID}`}>
                    {Invoice.HesaabNumber || InvoiceNumber || <Trans>(No reference number)</Trans>}
                  </Link>
                )}
                {Type === "ACCRECCREDIT" && (
                  <Link
                    to={`/accounts_receivable/credit_notes/edit/${InvoiceID}`}
                  >
                    {Invoice.HesaabNumber || InvoiceNumber || <Trans>(No reference number)</Trans>}
                  </Link>
                )}
              </td>
            )}
            <td>{formatDate(Date)}</td>
            {isInvoice && <td>{formatDate(DueDate)}</td>}
            {(isInvoice || isCreditNote) && (
              <td className="text-right">{formatAccountCurrency(Total)}</td>
            )}
            <td className="text-right">{formatCurrency(Amount)}</td>
          </tr>
        </tbody>
      </Table>
      <div className="d-flex justify-content-end">
        <div className="border-dark border-top border-bottom p-2 h3 d-flex">
          <span className="mr-5">
            <Trans>Total</Trans>
          </span>{" "}
          <span>{formatCurrency(state.Amount)}</span>
        </div>
      </div>
    </Page>
  );
};

export const enhance = compose(
  withProps((props) => {
    const { id } = useParams();
    const client = useXeroClient();
    const history = useHistory();
    const url = `payments`;
    let { data: state } = useQuery(url, () => client.get(url, { params: {
      raw: `PaymentID=GUID("${id}")`,
    }, }).get("data"), {
      suspense: true,
    });
    if (state){
      state=state[0]
    };
    if (state === undefined) {
      history.push(`/batch-payments/view/${id}`);
    };

    const { PaymentType } = state;

    const [document, documentType] = useMemo(() => {
      if (PaymentType === "ACCPAYPAYMENT") {
        return [RemittanceDocument, "REMITTANCE"];
      } else if (PaymentType === "ACCRECPAYMENT") {
        return [ReceiptDocument, "RECEIPT"];
      }
      return [null, null];
    }, [PaymentType]);

    return {
      ...props,
      state,
      document,
      documentType,
    };
  }),
  withDocumentRenderer({
    onDownload:
      ({ state, documentType }) =>
      (getDocument) => {
        getDocument(
          { item: state, contactID: state.Invoice.Contact.ContactID },
          `${documentType}_${state.Invoice.HesaabNumber || state.Invoice.InvoiceNumber}.pdf`
        );
      },
  })
);

export default enhance(Payment);
